/**
 * @ Author: Housefolios
 * @ Create Time: 2022-06-01 16:55:02
 * @ Modified by: Daniel Clark
 * @ Modified time: 2025-03-05 11:26:20
 * @ Description: Dialog for setting critical property fields
 */

import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

// Centralized lodash imports - much cleaner!
import {
  map,
  isEmpty,
  merge,
  remove,
  orderBy,
  filter,
  isUndefined,
  kebabCase,
  includes,
  replace,
  sortBy,
  meanBy,
  slice,
  get,
  split,
  ceil,
  head,
  round,
  debounce,
  isNil,
  cloneDeep,
  compact,
  sum,
  sumBy} from '@/utils/lodash'

import {Avatar, Button, Card, CardActions, CardContent, CardHeader, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel,  Grid, IconButton, InputAdornment, InputLabel, List, ListItem, ListItemText, Menu, MenuItem, Select, TextField, Tooltip, Typography, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import withSession from '@/housefolios-components/Session/withSession'
import { NumericFormat } from 'react-number-format'
import { createPepDealsauceEstimateFields } from '@/utils/rehabCalc'
import { useMutation } from '@apollo/client/react'
import { UPDATE_PROPERTY } from '@/housefolios-components/PropertyContainer/mutations'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'

function RehabCalcDialog(props) {
  const {
    open,
    onClose,
    property,
    affiliate,
    targetValue,
    assumptionsInput,
    changeMenu,
    onChangeAnalysis,
    onChangeAssumptions} = props

  const [accordion, setAccordion] = useState('ep')

  const toggleAccordion = (panel) => {
    if (accordion === panel) {
      setAccordion(null)
    } else {
      setAccordion(panel)
    }
  }

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 })

  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2 })

  const [affiliateEstimatesInput, setAffiliateEstimatesInput] =
    useState({})
  useEffect(() => {
    if (property?.estimates?.affiliate) {
      setAffiliateEstimatesInput(
        property?.estimates?.affiliate[
          targetValue.replace('CalcTotal', 'Cost')
        ] || {},
      )
    }
  }, [property.estimates, targetValue])

  const handleAffiliateEstimateChange = (event) => {
    const { name, type, value } = event.target
    console.log('estimatechange', name)
    let newAffiliateEstimates = cloneDeep(affiliateEstimatesInput)
    let newValue = value
    if (type === 'number') newValue = Number(value)
    else if (type === 'checkbox') {
      newValue = !newAffiliateEstimates[name]
    }
    newAffiliateEstimates[name] = newValue
    setAffiliateEstimatesInput(newAffiliateEstimates)
  }

  const [updateProperty] = useMutation(UPDATE_PROPERTY)

  const updateAffiliateEstimates = (
    property,
    affiliateEstimatesInput,
    assumptionsInput,
    targetValue,
  ) => {
    let affiliateEstimates =
      cloneDeep(property?.estimates?.affiliate) || {}
    affiliateEstimates[targetValue.replace('CalcTotal', 'Cost')] =
      affiliateEstimatesInput
    // let newAnalysisInput = {}
    const sumTotal = sum(
      map(
        createPepDealsauceEstimateFields(
          property,
          affiliateEstimatesInput,
          assumptionsInput,
        ),
        (section) => {
          return sumBy(section.fields, 'total')
        },
      ),
    )
    // newAnalysisInput[targetValue] = sumTotal
    updateProperty({
      variables: {
        id: property?._id,
        propertyInput: {
          // analysisInput: newAnalysisInput,
          estimates: {
            ...property.estimates,
            affiliate: {
              ...affiliateEstimates,
            },
          },
        },
      },
      // refetchQueries: [
      //   { query: GET_PROPERTY, variables: { id: property?._id } },
      // ],
    }).then(() => {
      //COMMENT: triggering changeMenu would cause the dialog to close
      // if (
      //   changeMenu &&
      //   property?.acquisition?.analysis[
      //     targetValue.replace('Cost', 'CalcTotal')
      //   ] !== sumTotal
      // ) {
      //   changeMenu(targetValue, sumTotal, 'affiliate')
      // }
      if (
        onChangeAnalysis &&
        property?.acquisition?.analysis[targetValue] !== sumTotal
      ) {
        onChangeAnalysis({
          target: {
            name: targetValue,
            value: sumTotal,
            type: 'number'}})
      }
    })
  }

  const debouncedUpdateAffiliateEstimates = useRef(
    debounce(
      (
        property,
        affiliateEstimatesInput,
        assumptionsInput,
        targetValue,
      ) =>
        updateAffiliateEstimates(
          property,
          affiliateEstimatesInput,
          assumptionsInput,
          targetValue,
        ),
      1000,
    ),
  ).current

  useEffect(() => {
    if (!isEmpty(property) && targetValue) {
      debouncedUpdateAffiliateEstimates(
        property,
        affiliateEstimatesInput,
        assumptionsInput,
        targetValue,
      )
    }
    return () => debouncedUpdateAffiliateEstimates.cancel()
  }, [
    property.estimates,
    affiliateEstimatesInput,
    assumptionsInput,
    targetValue,
  ])

  const renderInput = (name, value, type, booleanLabel = '') => {
    if (type === 'Boolean') {
      return (
        <Grid container direction="row" sx={{
          alignItems: "center"
        }}>
          <Grid>
            <Typography>{booleanLabel}</Typography>
          </Grid>
          <Grid data-hf-grid="keep">
            <Checkbox
              color="primary"
              key={name + '_key'}
              name={name}
              type="checkbox"
              value={value}
              checked={Boolean(value)}
              onChange={handleAffiliateEstimateChange}
            />
          </Grid>
          {/* <Grid data-hf-grid="keep" style={{ fontSize: 14 }}>
            {label}
          </Grid> */}
        </Grid>
      );
    } else if (type === 'number') {
      return (
        <Grid>
          <TextField
            name={name}
            variant="outlined"
            //margin="dense"
            value={value || ''}
            onChange={handleAffiliateEstimateChange}
            placeholder="Enter a Number"
            // label={label}
            type="number"
            // fullWidth={true}
          />
        </Grid>
      )
    } else if (type === 'disabled') {
      return (
        <Grid>
          <Typography>{value}</Typography>
        </Grid>
      )
    } else {
      return (
        <Grid>
          <NumericFormat
            customInput={TextField}
            thousandSeparator={true}
            prefix={'$'}
            // fullWidth={true}
            placeholder="Enter a Dollar Amount"
            isNumericString={true}
            decimalScale={2}
            key={name}
            name={name}
            value={round(value, 2) || ''}
            onValueChange={(values) => {
              // const { value } = values
              const event = {
                target: {
                  name: name,
                  value: values.value,
                  type: 'number'},
                preventDefault: () => {
                  return null
                }}
              if (Number(values.value) !== value) {
                // if (field.onChange) field.onChange(event)
                // else this.handleChange(event)
                onChangeAssumptions(event)
              }
            }}
            type={'text'}
            onFocus={(event) => {
              event.target.select()
            }}
            variant="outlined"
            // label={label}
          />
        </Grid>
      )
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle
        style={{
          backgroundColor: '#253053',
          padding: '15px',
          borderRadius: '5px',
          fontWeight: 'bold',
          color: '#FFF'}}
      >
        <Grid container direction="row">
          <Grid>{affiliate?.companyName} Estimate</Grid>
          <Typography style={{ flex: 1 }} />
          <Grid>
            {currencyFormat.format(
              sum(
                map(
                  createPepDealsauceEstimateFields(
                    property,
                    affiliateEstimatesInput,
                    assumptionsInput,
                  ),
                  (section) => {
                    return sumBy(section.fields, 'total')
                  },
                ),
              ),
            )}
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ marginTop: 20 }}>
        {map(
          createPepDealsauceEstimateFields(
            property,
            affiliateEstimatesInput,
            assumptionsInput,
          ),
          (section) => (
            <Card variant="outlined" key={section.panel} sx={(accordion === section.panel) && {
              "& [data-hf=\"accordion-toggle\"] .MuiButton-root": {
                borderBottomColor: "rgba(79, 80, 98, 0.3)",
                background: "#fff",
                color: "#070919 !important",
                "& [data-hf=\"accordion-icon\"]": { transform: "rotate(180deg)", color: "#368cff !important" },
              },
              "html[data-color-mode=\"dark\"] & [data-hf=\"accordion-toggle\"] .MuiButton-root": { borderBottomColor: "rgba(255, 255, 255, 0.12) !important", background: "#252525 !important", color: "rgba(255, 255, 255, 0.95) !important" },
            }}>
              <Card>
                <div
                  style={{ display: "flex", alignItems: "center", paddingTop: "0.75rem", paddingBottom: "0.75rem", paddingLeft: "1.25rem", paddingRight: "1.25rem", marginBottom: 0, backgroundColor: "#fff" }}
                  style={{
                    backgroundColor:
                      accordion === section.panel
                        ? '#8cc63f'
                        : '#ccc'}}
                >
                  <Box style={{ width: '100%' }} data-hf="panel-title" sx={{ width: "100%", display: "block" }}>
                    <Box data-hf="accordion-toggle" sx={{
                      "& .MuiButton-root": {
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                        alignItems: "center",
                        textAlign: "left",
                        fontWeight: 700,
                        color: "#3b3e66 !important",
                        textDecoration: "none !important",
                        textTransform: "none",
                        borderRadius: 0,
                        padding: "0.75rem 1.25rem !important",
                        borderBottom: "1px solid transparent",
                        background: "#f4f5fd",
                        "&:hover": {
                          background: "#f8f9ff !important",
                          color: "#070919 !important",
                          textDecoration: "none !important",
                          "& [data-hf=\"accordion-icon\"]": { color: "#368cff !important" },
                        },
                        "&:not(:disabled):hover": { transform: "translateY(0px)" },
                        "& [data-hf=\"accordion-icon\"]": {
                          transform: "rotate(90deg)",
                          transition: "all 0.2s ease-in-out",
                          marginLeft: "auto",
                          color: "#368cff !important",
                          overflow: "visible",
                        },
                      },
                      "html[data-color-mode=\"dark\"] & .MuiButton-root": {
                        background: "#2a2a2a !important",
                        color: "rgba(255, 255, 255, 0.9) !important",
                        "&:hover": { background: "#333 !important", color: "#fff !important" },
                      },
                    }}>
                      <Button
                        variant="text"
                        size="large"
                        sx={{
                          fontWeight: 700,
                          color: "#3b3e66",
                          textDecoration: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#4191ff",
                            textDecoration: "underline",
                            transform: "translateY(0px)",
                          },
                          "&:active": { textDecoration: "underline", boxShadow: "none" },
                          "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                          "& span": {
                            position: "relative",
                            "&::before": {
                              content: "\"\"",
                              position: "absolute",
                              width: "100%",
                              height: 2,
                              bottom: 0,
                              left: 0,
                              visibility: "hidden",
                              transform: "scaleX(0)",
                              transition: "all 0.3s ease-in-out 0s",
                            },
                            transition: "all 0.2s ease-in-out",
                          },
                          "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                          "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        onClick={() => toggleAccordion(section.panel)}
                        aria-expanded={accordion === section.panel}
                        style={{ width: '100%' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center'}}
                        >
                          {section.sectionIcon}
                          <span style={{ paddingLeft: '5px' }}>
                            {section.sectionName}
                          </span>
                        </div>
                        <Typography style={{ flex: 1 }} />
                        {!section.noTotal && (
                          <Typography style={{ paddingRight: 10 }}>
                            {currencyFormat.format(
                              sumBy(section.fields, 'total'),
                            )}
                          </Typography>
                        )}
                        <FontAwesomeIcon icon={['fas', 'angle-up']} sx={{ fontSize: "1.265rem !important" }} data-hf="accordion-icon" />
                      </Button>
                    </Box>
                  </Box>
                </div>
                <Collapse in={accordion === section.panel}>
                  <CardContent
                    style={{
                      marginTop: -10,
                      marginBottom: -10,
                      marginLeft: 10,
                      marginRight: 10}}
                  >
                    <List
                      component="div"
                      sx={{
                        paddingTop: 0,
                        paddingBottom: 0,
                        "& .MuiListItem-root, & .MuiListItemButton-root": {
                          width: "100%",
                          padding: "0.75rem 1.25rem",
                          backgroundColor: "#fff",
                          border: "1px solid rgba(0, 0, 0, 0.125)",
                          borderRadius: 0,
                          margin: 0,
                          borderWidth: "0 0 1px",
                          "&.MuiListItem-button": {
                            borderRadius: 0,
                            borderWidth: "0 0 1px",
                            "& .MuiTouchRipple-root": { opacity: 0.2 },
                          },
                          "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
                          "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
                          "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
                          "&:first-of-type": { borderTop: "0 !important" },
                          "&:last-child": { borderBottom: "0 !important" },
                        },
                      }}
                    >
                      {map(section.fields, (line) => (
                        <ListItem key={line.label}>
                          <Grid
                            container
                            spacing={2}
                            direction={'row'}
                            style={{ width: '100%' }}
                            sx={{
                              alignItems: 'center'
                            }}
                          >
                            <Grid>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'}}
                              >
                                <Typography>{line.label}</Typography>
                                {line.tooltip && (
                                  <Tooltip
                                    title={line.tooltip}
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      style={{ padding: '2px' }}
                                    >
                                      <FontAwesomeIcon
                                        icon={[
                                          'far',
                                          'circle-question',
                                        ]}
                                        style={{
                                          fontSize: '14px',
                                          color: '#666'}}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </div>
                            </Grid>
                            <Typography style={{ flex: 1 }} />
                            {line.secondType &&
                              renderInput(
                                line.secondName,
                                line.secondValue,
                                line.secondType,
                                line.secondBooleanLabel,
                              )}
                            {line.type &&
                              renderInput(
                                line.name,
                                line.value,
                                line.type,
                                line.booleanLabel,
                              )}
                            {line.assumptionName &&
                              renderInput(
                                line.assumptionName,
                                line.assumptionValue,
                                'assumption',
                              )}
                            {!isNil(line.total) && (
                              <Grid size={2}>
                                <Typography
                                  style={{ textAlign: 'end' }}
                                >
                                  {currencyFormat.format(line.total)}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </ListItem>
                      ))}
                      {section.notesName && (
                        <ListItem>
                          <TextField
                            name={section.notesName}
                            variant="outlined"
                            //margin="dense"
                            value={section.notes || ''}
                            onChange={handleAffiliateEstimateChange}
                            placeholder="Notes"
                            label={'Notes'}
                            type="text"
                            multiline
                            minRows={4}
                            fullWidth={true}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Collapse>
              </Card>
            </Card>
          ),
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
        {/* <Button
            variant="contained"
            disabled={!selectedComp}
            onClick={() => {
              //Grabs estavl as the menu variable is afterRepair value which comp doesn't have
              changeMenu(
                menuTarget || menu,
                selectedComp['estval'],
                //selectedComp[menu],
                'compSearch',
              )
              setOpenCompDialog(false)
            }}
          >
            Use this total
          </Button> */}
      </DialogActions>
    </Dialog>
  );
}

export default withSession(RehabCalcDialog)
